import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { QrCode, ChartColumn, Trash2, ChevronRight, UserCheck } from 'lucide-react';
import { SlideTransition } from '../../../../components/Transition/Transition';
import {
  applyQuizToRecruitProfile,
  careerPartTwoOf,
  careerResultOf,
  clearRecruitQuiz,
  quizMergeMessage
} from '../recruitQuizStorage';
import type { RecruitQuizPartTwo } from '../recruitQuizStorage';
import { careerDreamLabels } from '../../../CareerQuiz/careerDreamData';
import { partTwoAdviserNotes, partTwoLabels } from '../../../CareerQuiz/careerPartTwoData';
import { useQuizInvitations } from '../../../CoverQuiz/coverQuizStorage';
import QuizShareSection from './QuizShareSection';
import type { IntlLike } from '../clientPresentation';
import { recruitProfileLabelId } from './RecruitProfileChips';
import { DISC_EMOJI, discName, isDiscLetter } from '../../../../constants/disc';
import { quizLangOf } from '../../../CoverQuiz/quizLang';
import type { QuizLang } from '../../../CoverQuiz/quizLang';
import { pickCopy } from '../../../../utils/localeCopy';
import './RecruitQuizDialog.scss';

const SHARE_COPY = {
  'zh-Hant-HK': {
    heading: '📱 現場面談掃碼即做',
    desc: '候選人用自己的手機做也可以，免登入 2 分鐘，完成後會自動顯示在這裡。',
    copyLabel: '複製專屬連結',
    copiedLabel: '已複製連結！',
    previewLabel: '預覽問卷'
  },
  zh: {
    heading: '📱 现场面谈扫码即做',
    desc: '候选人用自己的手机做也可以，免登录 2 分钟，完成后会自动显示在这里。',
    copyLabel: '复制专属链接',
    copiedLabel: '已复制链接！',
    previewLabel: '预览问卷'
  },
  en: {
    heading: '📱 Scan and take it at the meeting',
    desc: 'The candidate can also take it on their own phone: no login, 2 minutes, and the result shows up here automatically.',
    copyLabel: 'Copy personal link',
    copiedLabel: 'Link copied!',
    previewLabel: 'Preview quiz'
  }
};

// Per part: 1 is the whole 職涯天賦測驗; 2 is 第二部分 (理想事業藍圖) on its own.
const PART_COPY = {
  'zh-Hant-HK': {
    1: {
      title: '職涯天賦測驗 (免登入)',
      subtitle: '填寫完成後自動解析痛點與 DISC',
      emptyTitle: '候選人尚未完成測驗',
      share: SHARE_COPY['zh-Hant-HK'],
      invite: (name: string, url: string) =>
        `Hi ${name}！最近我們團隊推出了一個很有趣的 2 分鐘職場天賦與能量微測驗，可以快速測出你的做事風格與隱藏優勢，完成後即時獲得專屬分析報告！\n很值得參考，你可以入內試玩一下：${url}`
    },
    2: {
      title: '職涯測驗 第二部分 (免登入)',
      subtitle: '他想要的團隊、人脈、家人看法和想問你的問題',
      emptyTitle: '候選人尚未完成第二部分',
      share: {
        ...SHARE_COPY['zh-Hant-HK'],
        desc: '候選人用自己的手機做也可以，免登入約 2 分鐘，完成後會自動顯示在這裡。'
      },
      invite: (name: string, url: string) =>
        `Hi ${name}！上次的職涯測驗之後，這裡有第二部分：用 2 分鐘描繪你的理想事業藍圖，下次見面我們一起看：${url}`
    }
  },
  zh: {
    1: {
      title: '职涯天赋测验 (免登录)',
      subtitle: '填写完成后自动解析痛点与 DISC',
      emptyTitle: '候选人尚未完成测验',
      share: SHARE_COPY.zh,
      invite: (name: string, url: string) =>
        `Hi ${name}！最近我们团队推出了一个很有趣的 2 分钟职场天赋与能量微测验，可以快速测出你的做事风格与隐藏优势，完成后即时获得专属分析报告！\n很值得参考，你可以进去试玩一下：${url}`
    },
    2: {
      title: '职涯测验 第二部分 (免登录)',
      subtitle: '他想要的团队、人脉、家人看法和想问你的问题',
      emptyTitle: '候选人尚未完成第二部分',
      share: { ...SHARE_COPY.zh, desc: '候选人用自己的手机做也可以，免登录约 2 分钟，完成后会自动显示在这里。' },
      invite: (name: string, url: string) =>
        `Hi ${name}！上次的职涯测验之后，这里有第二部分：用 2 分钟描绘你的理想事业蓝图，下次见面我们一起看：${url}`
    }
  },
  en: {
    1: {
      title: 'Career talent quiz (no login)',
      subtitle: 'Pain points and DISC are analysed as soon as it is done',
      emptyTitle: "The candidate hasn't taken the quiz yet",
      share: SHARE_COPY.en,
      invite: (name: string, url: string) =>
        `Hi ${name}! Our team has a fun 2-minute quiz on work talents and energy. It shows your working style and hidden strengths, with a personal report the moment you finish!\nWorth a look, give it a try: ${url}`
    },
    2: {
      title: 'Career quiz part 2 (no login)',
      subtitle: 'The team they want, their network, family views and questions for you',
      emptyTitle: "The candidate hasn't finished part 2 yet",
      share: {
        ...SHARE_COPY.en,
        desc: 'The candidate can also take it on their own phone: no login, about 2 minutes, and the answers show up here automatically.'
      },
      invite: (name: string, url: string) =>
        `Hi ${name}! Following the career quiz, here is part 2: sketch your ideal career in 2 minutes, and we can go through it together next time: ${url}`
    }
  }
};

const UI_COPY = {
  'zh-Hant-HK': {
    partTwoTitle: '第二部分：理想事業藍圖',
    completedAt: (time: string) => `完成於 ${time}`,
    team: '🤝 想要的團隊：',
    topics: '💡 下次見面話題：',
    partTwoNote: '按這些答案，由你自己判斷；這裡沒有評分。',
    clearConfirm: '確定要清除此候選人的測驗紀錄嗎？清除後可重新測試。',
    statusDone: '✅ 已完成測驗',
    statusWaiting: '⏳ 待候選人作答',
    candidate: '候選人：',
    emptyDesc: '將連結發送給候選人，或現場掃描 QR Code 填寫。候選人用自己的手機做也可以，完成後這裡會自動顯示',
    emptyDescPart2: '他的答案。',
    emptyDescPart1: '分析結果與同步按鈕。',
    resultTitle: '候選人作答分析結果',
    path: '職涯路線：',
    graduate: '🎓 畢業生',
    experienced: '💼 有經驗',
    onFile: '檔案記錄：',
    targetIncome: '期望月入',
    painPoints: '⚡ 自選痛點：',
    strengths: '💪 核心天賦：',
    dreamLife: '🌅 想要的生活：',
    dream: '夢想：',
    identity: '3 年後：',
    concerns: '🛡️ 轉型顧慮：',
    noAnswer: '未有作答',
    afterPartTwo: '第二部分完成後顯示',
    sync: '同步至招募檔案',
    resync: '第二部分有新答案，再同步一次可帶入「想要的支援」',
    syncedAt: (time: string) => `已於 ${time} 同步至招募檔案`,
    openIdea: '前往招募 IDEA 策略',
    clear: '清除作答紀錄'
  },
  zh: {
    partTwoTitle: '第二部分：理想事业蓝图',
    completedAt: (time: string) => `完成于 ${time}`,
    team: '🤝 想要的团队：',
    topics: '💡 下次见面话题：',
    partTwoNote: '按这些答案，由你自己判断；这里没有评分。',
    clearConfirm: '确定要清除此候选人的测验记录吗？清除后可重新测试。',
    statusDone: '✅ 已完成测验',
    statusWaiting: '⏳ 待候选人作答',
    candidate: '候选人：',
    emptyDesc: '将链接发送给候选人，或现场扫描二维码填写。候选人用自己的手机做也可以，完成后这里会自动显示',
    emptyDescPart2: '他的答案。',
    emptyDescPart1: '分析结果与同步按钮。',
    resultTitle: '候选人作答分析结果',
    path: '职涯路线：',
    graduate: '🎓 毕业生',
    experienced: '💼 有经验',
    onFile: '档案记录：',
    targetIncome: '期望月入',
    painPoints: '⚡ 自选痛点：',
    strengths: '💪 核心天赋：',
    dreamLife: '🌅 想要的生活：',
    dream: '梦想：',
    identity: '3 年后：',
    concerns: '🛡️ 转型顾虑：',
    noAnswer: '未有作答',
    afterPartTwo: '第二部分完成后显示',
    sync: '同步至招募档案',
    resync: '第二部分有新答案，再同步一次可带入「想要的支持」',
    syncedAt: (time: string) => `已于 ${time} 同步至招募档案`,
    openIdea: '前往招募 IDEA 策略',
    clear: '清除作答记录'
  },
  en: {
    partTwoTitle: 'Part 2: ideal career blueprint',
    completedAt: (time: string) => `Completed ${time}`,
    team: '🤝 Team they want: ',
    topics: '💡 Topics for next meeting: ',
    partTwoNote: 'Use these answers for your own judgement; nothing here is scored.',
    clearConfirm: "Clear this candidate's quiz record? They can take it again afterwards.",
    statusDone: '✅ Quiz completed',
    statusWaiting: '⏳ Waiting for candidate',
    candidate: 'Candidate: ',
    emptyDesc:
      'Send the link to the candidate, or have them scan the QR code at the meeting. They can also take it on their own phone; once done, this shows ',
    emptyDescPart2: 'their answers.',
    emptyDescPart1: 'the analysis and the sync button.',
    resultTitle: "Candidate's quiz analysis",
    path: 'Career path: ',
    graduate: '🎓 Graduate',
    experienced: '💼 Experienced',
    onFile: 'On file: ',
    targetIncome: 'Target monthly income',
    painPoints: '⚡ Pain points: ',
    strengths: '💪 Core talents: ',
    dreamLife: '🌅 Life they want: ',
    dream: 'Dream: ',
    identity: 'In 3 years: ',
    concerns: '🛡️ Concerns about switching: ',
    noAnswer: 'Not answered',
    afterPartTwo: 'Shown after part 2',
    sync: 'Sync to recruit profile',
    resync: 'Part 2 has new answers. Sync again to bring in "support wanted"',
    syncedAt: (time: string) => `Synced to recruit profile on ${time}`,
    openIdea: 'Open recruit IDEA',
    clear: 'Clear answers'
  }
};

type RecruitQuizUi = typeof UI_COPY.en;

/** 第二部分's answers as the adviser reads them: grouped by the radar items, never scored. */
const PartTwoCard = ({ partTwo, ui, lang }: { partTwo: RecruitQuizPartTwo; ui: RecruitQuizUi; lang: QuizLang }) => {
  const notes = partTwoAdviserNotes(partTwo.answers, lang);
  const team = partTwoLabels(partTwo.answers).team;
  return (
    <div className="rqd-result-card rqd-anim-item" data-testid="recruit-quiz-part2">
      <div className="rqd-result-head">
        <div className="rqd-result-head-left">
          <ChartColumn size={16} color="#475569" />
          <strong>{ui.partTwoTitle}</strong>
        </div>
        <span className="rqd-result-time">
          {ui.completedAt(moment(partTwo.completedAt).format('YYYY-MM-DD HH:mm'))}
        </span>
      </div>
      <div className="rqd-tags-col">
        {team && (
          <div className="rqd-tag-row">
            <span className="rqd-tag-label">{ui.team}</span>
            <div className="rqd-tag-list">
              <span className="rqd-tag">{team.label[lang]}</span>
            </div>
          </div>
        )}
        {notes.rows.map(row => (
          <div key={row.axis} className="rqd-tag-row" data-testid="recruit-quiz-part2-row">
            <span className="rqd-tag-label">{row.axis}：</span>
            <span className="rqd-part2-text">{row.text}</span>
          </div>
        ))}
        {notes.flags.map(flag => (
          <p key={flag} className="rqd-part2-flag" data-testid="recruit-quiz-part2-flag">
            ⚠️ {flag}
          </p>
        ))}
        {notes.topics.length > 0 && (
          <div className="rqd-tag-row">
            <span className="rqd-tag-label">{ui.topics}</span>
            <div className="rqd-tag-list">
              {notes.topics.map(topic => (
                <span key={topic} className="rqd-tag">
                  {topic}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="rqd-part2-note">{ui.partTwoNote}</p>
    </div>
  );
};

export interface RecruitQuizDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  /** The default display name sent with a new invitation — the factsheet's `name`, never the nickname. */
  candidateName?: string;
  /** Which part a new link starts; 1 (the whole quiz) by default. */
  part?: 1 | 2;
  intl: IntlLike;
  /** The DISC letter on file: the sync sets the quiz's letter only when this is empty. */
  currentDisc?: string | null;
  /** Returns false when the change was not allowed. */
  onChangePersonality?: (type: string) => boolean | void;
  onOpenRecruitIdea?: () => void;
}

export const RecruitQuizDialog = (props: RecruitQuizDialogProps) => {
  const {
    open,
    onClose,
    clientId,
    candidateName,
    intl,
    currentDisc,
    onChangePersonality,
    onOpenRecruitIdea,
    part = 1
  } = props;
  const copy = pickCopy(PART_COPY, intl.locale)[part];
  const ui = pickCopy(UI_COPY, intl.locale);
  const lang = quizLangOf(intl.locale);
  const t = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);

  const theme = useTheme();
  // Phones get a full-screen sheet — the QR scan use case is face-to-face on mobile
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    list,
    factsheetId,
    failed: listStale
  } = useQuizInvitations(clientId, 'career', { pollMs: open ? 5000 : undefined, load: open });
  const quiz = useMemo(() => careerResultOf(clientId, list?.completed), [clientId, list]);
  const partTwo = useMemo(() => careerPartTwoOf(list?.completed), [list]);
  const pending = list?.pending.find(q => q.part === part);
  const done = part === 2 ? Boolean(partTwo) : Boolean(quiz);
  const dream = quiz ? careerDreamLabels(quiz) : null;
  const [mergeMessage, setMergeMessage] = useState<string | null>(null);

  // A message about one candidate's sync never shows on the next candidate's result.
  const quizId = quiz?.id;
  useEffect(() => setMergeMessage(null), [clientId, quizId]);

  const inviteText = (url: string, lang: QuizLang) => pickCopy(PART_COPY, lang)[part].invite(candidateName || '', url);

  const handleApply = () => {
    if (!quiz) return;
    setMergeMessage(
      quizMergeMessage(applyQuizToRecruitProfile(clientId, quiz, { currentDisc, onChangePersonality }), intl.locale)
    );
  };

  const handleClear = () => {
    if (window.confirm(ui.clearConfirm)) {
      void clearRecruitQuiz(clientId);
    }
  };

  const discInfo = isDiscLetter(quiz?.discLetter)
    ? { letter: quiz.discLetter, name: discName(quiz.discLetter, intl.locale), emoji: DISC_EMOJI[quiz.discLetter] }
    : null;
  // When the file already says otherwise, show both: the sync keeps the recruiter's letter.
  const discOnFile = isDiscLetter(currentDisc) && currentDisc !== discInfo?.letter ? currentDisc : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      className="clientv2-recruit-quiz-modal"
      slots={{
        transition: SlideTransition
      }}
      slotProps={{
        paper: {
          style: {
            borderRadius: isMobile ? 0 : 20,
            background: '#ffffff',
            overflow: 'hidden'
          }
        }
      }}
    >
      <style>{`
        .clientv2-recruit-quiz-modal .emergency_reference--qrcode,
        .clientv2-recruit-quiz-modal .qr-code-wrapper,
        .clientv2-recruit-quiz-modal .qr-code-refer {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          overflow: hidden !important;
        }
        .clientv2-recruit-quiz-modal img#qr-code {
          width: 104px !important;
          height: 104px !important;
          max-width: 104px !important;
          max-height: 104px !important;
          object-fit: contain !important;
          display: block !important;
        }
        @media (max-width: 600px) {
          .clientv2-recruit-quiz-modal img#qr-code {
            width: 132px !important;
            height: 132px !important;
            max-width: 132px !important;
            max-height: 132px !important;
          }
        }
      `}</style>

      <DialogTitle className="clientv2-quiz-dialog-title rqd-title">
        <div className="rqd-title-row">
          <div className="rqd-title-left">
            <span className="rqd-title-icon">
              <QrCode size={22} />
            </span>
            <strong className="rqd-title-text">{copy.title}</strong>
          </div>
          <span className={`rqd-status-pill ${done ? 'is-done' : ''}`} data-testid="recruit-quiz-status">
            {done ? ui.statusDone : ui.statusWaiting}
          </span>
        </div>
        <p className="rqd-subtitle">
          {ui.candidate}
          <strong>{candidateName}</strong> · {copy.subtitle}
        </p>
      </DialogTitle>

      <DialogContent dividers className="rqd-content">
        {/* SECTION 1: Face-to-Face & Link Share */}
        <QuizShareSection
          type="career"
          part={part}
          factsheetId={factsheetId}
          pending={pending}
          done={done}
          defaultName={candidateName}
          inviteText={inviteText}
          copy={copy.share}
          testIdPrefix="recruit-quiz"
          stale={listStale}
          locale={intl.locale}
        />

        {!done && (
          <div className="rqd-empty-state rqd-anim-item">
            <p className="rqd-empty-title">{copy.emptyTitle}</p>
            <p className="rqd-empty-desc">
              {ui.emptyDesc}
              {part === 2 ? ui.emptyDescPart2 : ui.emptyDescPart1}
            </p>
          </div>
        )}

        {part === 2 && partTwo && <PartTwoCard partTwo={partTwo} ui={ui} lang={lang} />}

        {/* SECTION 2: Quiz Results & Sync to Recruitment Profile */}
        {quiz && (
          <div className="rqd-result-card rqd-anim-item">
            <div className="rqd-result-head">
              <div className="rqd-result-head-left">
                <ChartColumn size={16} color="#475569" />
                <strong>{ui.resultTitle}</strong>
              </div>
              <span className="rqd-result-time">
                {ui.completedAt(moment(quiz.completedAt).format('YYYY-MM-DD HH:mm'))}
              </span>
            </div>

            {/* DISC Archetype Card */}
            {discInfo && (
              <div className="rqd-disc-card">
                <span className="rqd-disc-emoji">{discInfo.emoji}</span>
                <div className="rqd-disc-info">
                  <strong>
                    {discInfo.name} ({discInfo.letter})
                  </strong>
                  <span>
                    {ui.path}
                    {quiz.path === 'graduate' ? ui.graduate : ui.experienced}
                  </span>
                  {discOnFile && (
                    <span data-testid="recruit-quiz-disc-on-file">
                      {ui.onFile}
                      {discName(discOnFile, intl.locale)} ({discOnFile})
                    </span>
                  )}
                </div>
                {quiz.targetIncome && (
                  <div className="rqd-disc-income">
                    <span>{ui.targetIncome}</span>
                    <strong>${quiz.targetIncome.toLocaleString()}</strong>
                    {dream?.horizon && <span data-testid="recruit-quiz-horizon">{dream.horizon.label[lang]}</span>}
                  </div>
                )}
              </div>
            )}

            {/* Pain points, Concerns, Strengths Tags */}
            <div className="rqd-tags-col">
              <div className="rqd-tag-row">
                <span className="rqd-tag-label">{ui.painPoints}</span>
                <div className="rqd-tag-list">
                  {quiz.painPoints.map(k => (
                    <span key={k} className="rqd-tag">
                      {t(`clientv2-recruit-pain-${k}`)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rqd-tag-row">
                <span className="rqd-tag-label">{ui.strengths}</span>
                <div className="rqd-tag-list">
                  {quiz.strengths.map(k => (
                    <span key={k} className="rqd-tag">
                      {t(recruitProfileLabelId('strengths', k))}
                    </span>
                  ))}
                </div>
              </div>

              {dream && (dream.idealDay.length > 0 || dream.dream || dream.identity) && (
                <div className="rqd-tag-row" data-testid="recruit-quiz-dream">
                  <span className="rqd-tag-label">{ui.dreamLife}</span>
                  <div className="rqd-tag-list">
                    {dream.idealDay.map(o => (
                      <span key={o.key} className="rqd-tag">
                        {o.icon} {o.label[lang]}
                      </span>
                    ))}
                    {dream.dream && (
                      <span className="rqd-tag">
                        {dream.dream.icon} {ui.dream}
                        {dream.dream.label[lang]}
                      </span>
                    )}
                    {dream.identity && (
                      <span className="rqd-tag">
                        {dream.identity.icon} {ui.identity}
                        {dream.identity.label[lang]}
                      </span>
                    )}
                  </div>
                </div>
              )}

              <div className="rqd-tag-row">
                <span className="rqd-tag-label">{ui.concerns}</span>
                <div className="rqd-tag-list">
                  {quiz.concerns.length > 0 ? (
                    quiz.concerns.map(k => (
                      <span key={k} className="rqd-tag">
                        {t(`clientv2-recruit-concern-${k}`)}
                      </span>
                    ))
                  ) : (
                    // Asked in 第二部分 since 2026-10-01: promise it only while part 2 is still to come.
                    <span className="rqd-tag-muted" data-testid="rqd-concerns-pending">
                      {partTwo ? ui.noAnswer : ui.afterPartTwo}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Sync Action Area */}
            <div className="rqd-sync-row">
              <div className="rqd-sync-left">
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={handleApply}
                  startIcon={<UserCheck size={16} />}
                  style={{ fontWeight: 700, borderRadius: 10, textTransform: 'none' }}
                >
                  {ui.sync}
                </Button>
                {mergeMessage ? (
                  <small className="rqd-applied-notice" data-testid="recruit-quiz-merge-message">
                    {mergeMessage}
                  </small>
                ) : quiz.partTwoSinceApplied ? (
                  <small className="rqd-applied-notice" data-testid="recruit-quiz-resync">
                    {ui.resync}
                  </small>
                ) : (
                  quiz.isApplied && (
                    <small className="rqd-applied-at">
                      {ui.syncedAt(moment(quiz.appliedAt).format('YYYY-MM-DD HH:mm'))}
                    </small>
                  )
                )}
              </div>

              {onOpenRecruitIdea && (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={() => {
                    onClose();
                    onOpenRecruitIdea();
                  }}
                  endIcon={<ChevronRight size={14} />}
                  style={{ fontSize: 12, textTransform: 'none', fontWeight: 600 }}
                >
                  {ui.openIdea}
                </Button>
              )}
            </div>
          </div>
        )}

        {part === 1 && partTwo && <PartTwoCard partTwo={partTwo} ui={ui} lang={lang} />}
      </DialogContent>

      <DialogActions className="rqd-actions">
        {quiz || partTwo ? (
          <Button
            size="small"
            onClick={handleClear}
            startIcon={<Trash2 size={13} />}
            style={{ color: '#94a3b8', fontSize: 12, textTransform: 'none' }}
          >
            {ui.clear}
          </Button>
        ) : (
          <div />
        )}
        <Button onClick={onClose} color="primary" variant="outlined" style={{ borderRadius: 8 }}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecruitQuizDialog;
